import { defaultSchema } from 'rehype-sanitize';
import type { Options } from 'rehype-sanitize';

/**
 * Security configuration for rich text rendering.
 *
 * Blocks XSS via rehype-sanitize:
 * - Tag whitelist: safe inline + block elements (paragraphs, lists, blockquote,
 *   hr, code blocks, GFM tables). Headings (h1–h6) are opt-in via the long-form
 *   schema only.
 * - Attribute whitelist: links keep only href/target/rel; all other tags fall
 *   back to rehype-sanitize's safe defaults. `style` is not among them and must
 *   not be added: inline style on arbitrary elements is a UI-redress surface.
 * - Protocol whitelist: href is narrowed to http/https/mailto (drops the
 *   default irc/xmpp/etc.); the remaining protocol guards (cite/src/...) inherit
 *   rehype-sanitize's defaults by spreading them rather than replacing the map.
 */
const BASE_RICH_TEXT_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'hr',
  // GFM tables, in the base schema rather than the long-form one: a table
  // cannot enlarge text or emit a page-level heading, so it is not a
  // visual-shouting vector, and a schedule is exactly what someone writes in a
  // post. Without the whole set the sanitizer unwraps the tags and keeps the
  // cell text, so `| 18:00 | doors |` renders as one run-on line.
  // Only the elements remark-gfm actually emits: `tfoot`, `caption` and
  // `colgroup` are unreachable from markdown, and raw HTML never reaches the
  // sanitizer (no rehype-raw), so nothing else can produce them.
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

export const richTextSanitizeSchema: Options = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: ['href', 'target', 'rel'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
  tagNames: BASE_RICH_TEXT_TAGS,
};

// Long-form schema: the base schema + h1–h6. Derived from `richTextSanitizeSchema`
// so the attribute/protocol guards never drift between the two.
// Reserved for owner-authored, structural copy: Space/Community "About"
// sections and event descriptions — destination pages whose author already owns
// the surface. Excluded from feed-adjacent surfaces (posts, chat, profile
// notes) to prevent visual-shouting / formatting clickbait.
export const richTextLongFormSanitizeSchema: Options = {
  ...richTextSanitizeSchema,
  tagNames: [...BASE_RICH_TEXT_TAGS, 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
};
